import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  LIST_CONVERSATIONS,
  UPDATE_CONVERSATION,
} from "../../services/graphql/conversation-operations";
import { LoadingSpinner } from "../../components/ui/loading-spinner";
import { ConversationCard } from "../../components/conversation/conversation-card";
import { useGlobalClient } from "../../hooks/use-global-client";

export function ConversationsPage() {
  // State for loading more conversations and showing archived
  const [loadingMore, setLoadingMore] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Global client context
  const {
    selectedClient,
    loading: clientLoading,
    isSuperAdmin,
    availableClients,
  } = useGlobalClient();

  // For super admins, just wait for client selection and loading to complete
  // The selectedClient validation happens in useGlobalClient
  // For regular users, just wait for client loading to complete
  const shouldFetchData = !!selectedClient && !clientLoading;

  // Debug logging for conversations page
  console.log("🔍 ConversationsPage Debug:", {
    selectedClient: selectedClient,
    selectedClientId: selectedClient?.id,
    clientLoading: clientLoading,
    clientsCount: availableClients?.length || 0,
    clientsExists: !!availableClients,
    shouldFetchData: shouldFetchData,
    isSuperAdmin: isSuperAdmin,
    showArchived: showArchived,
    timestamp: new Date().toISOString(),
  });

  // Fetch conversations
  const { data, loading, error, refetch, fetchMore } = useQuery(
    LIST_CONVERSATIONS,
    {
      variables: {
        limit: 50, // Fetch more to account for filtering
        filter: showArchived ? { status: "ARCHIVED" } : {}, // Filter based on showArchived state
      },
      skip: !shouldFetchData,
      fetchPolicy: "network-only", // Force server call, bypass cache
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    }
  );

  // Debug logging for query results
  console.log("🔍 ConversationsPage Query Debug:", {
    data: data,
    listConversationsData: data?.listConversations,
    conversationsItems: data?.listConversations?.items,
    itemsCount: data?.listConversations?.items?.length || 0,
    loading: loading,
    error: error,
    querySkipped: !shouldFetchData,
  });

  // Filter conversations based on archive status
  const filteredConversations = React.useMemo(() => {
    const allConversations = data?.listConversations?.items || [];
    if (showArchived) {
      return allConversations.filter((conv: any) => conv.status === "ARCHIVED");
    } else {
      return allConversations.filter((conv: any) => conv.status !== "ARCHIVED");
    }
  }, [data?.listConversations?.items, showArchived]);

  const conversations = filteredConversations;

  // Archive mutation
  const [updateConversation] = useMutation(UPDATE_CONVERSATION, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.error("Error archiving conversation:", error);
      alert("Failed to archive conversation. Please try again.");
    },
  });

  // Handle archive action
  const handleArchive = async (conversationId: string) => {
    if (confirm("Are you sure you want to archive this conversation?")) {
      try {
        await updateConversation({
          variables: {
            input: {
              id: conversationId,
              status: "ARCHIVED",
            },
          },
        });
      } catch (error) {
        console.error("Error archiving conversation:", error);
      }
    }
  };

  // Handle unarchive action
  const handleUnarchive = async (conversationId: string) => {
    if (confirm("Are you sure you want to unarchive this conversation?")) {
      try {
        await updateConversation({
          variables: {
            input: {
              id: conversationId,
              status: "ACTIVE",
            },
          },
        });
      } catch (error) {
        console.error("Error unarchiving conversation:", error);
      }
    }
  };

  // Handle load more conversations
  const handleLoadMore = async () => {
    if (!data?.listConversations?.nextToken || loadingMore) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          nextToken: data.listConversations.nextToken,
          limit: 10,
          filter: showArchived ? { status: "ARCHIVED" } : {}, // Filter based on showArchived state
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            listConversations: {
              ...fetchMoreResult.listConversations,
              items: [
                ...prev.listConversations.items,
                ...fetchMoreResult.listConversations.items,
              ],
            },
          };
        },
      });
    } catch (error) {
      console.error("Error loading more conversations:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  console.log("Conversations data:", { data, conversations, loading, error });

  // Loading state
  if (clientLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Conversations</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Failed to load conversations
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => refetch()}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Conversations</h1>
        <p className="mt-2 text-gray-600">
          Build conversations by attaching reusable components: clients, assets,
          QR codes/links, auto-responders, and destinations.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Active Conversations
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {" "}
                {conversations?.length}
              </p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Interactions
              </p>
              <p className="text-2xl font-semibold text-gray-900">2,847</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Conversion Rate
              </p>
              <p className="text-2xl font-semibold text-gray-900">24.3%</p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Conversation Builder Section */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Conversation Builder
            </h2>
            <Link
              to="/conversations/create"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create New Conversation
            </Link>
          </div>
        </div>
        {conversations.filter((conversation: any) => !conversation.archived)
          .length === 0 &&
          !showArchived && (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Build A Conversation
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Create conversations by selecting and combining reusable
                  components. No need to recreate assets, links, or
                  auto-responders.
                </p>
                <Link
                  to="/conversations/create"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Start Building
                </Link>
              </div>
            </div>
          )}
      </div>

      {/* Recent Conversations */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {showArchived ? "Archived Conversations" : "Active Conversations"}
            </h2>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Show archived
                </span>
              </label>
            </div>
          </div>
        </div>
        <div className="p-6">
          {conversations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No conversations yet
              </h3>
              <p className="text-gray-500 mb-4">
                Create your first conversation to get started.
              </p>
              <Link
                to="/conversations/create"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Conversation
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {conversations.map((conversation: any) => (
                <ConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onRefetch={refetch}
                />
              ))}

              {data?.listConversations?.nextToken && (
                <div className="text-center py-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {loadingMore ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        Load More
                        <svg
                          className="ml-1 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
